<template>
  <section class="boost pt-10 pb-12 md:pt-10 md:pb-[186px]">
    <div class="mx-auto min-w-[320px] max-w-container px-4">
      <ul class="list-none p-0 m-0 flex flex-col gap-0">
        <li
          v-for="(step, i) in boostList"
          :key="step.id"
          class="boost-set-item"
        >
          <div
            class="boost-set-item-grid grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-16 lg:gap-24 items-start"
            :class="i % 2 === 1 ? 'md:grid-flow-dense' : ''"
          >
            <div
              class="boost-content order-1 md:order-none"
              :class="[
                i % 2 === 1
                  ? 'md:col-start-2 md:row-start-1 md:text-right'
                  : '',
              ]"
            >
              <h2
                class="font-semibold text-2xl md:text-4xl mb-4 mt-0"
                :class="i % 2 === 1 ? 'text-left md:text-right' : 'text-left'"
              >
                {{ i + 1 }}. {{ step.title }}
              </h2>
              <div
                class="prose prose-gray max-w-none prose-p:mb-3 prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-success prose-a:underline hover:prose-a:no-underline"
                :class="i % 2 === 1 ? 'text-left md:text-right' : 'text-left'"
                v-html="step.textContent"
              />
            </div>
            <div
              class="boost-img-wrap order-2 md:order-none flex justify-center md:block"
              :class="[
                i % 2 === 1 ? 'md:col-start-1 md:row-start-1' : 'md:justify-self-end',
              ]"
            >
              <NuxtImg
                :src="step.img"
                :alt="step.alt"
                :width="step.width"
                :height="step.height"
                class="w-full max-w-[430px] h-auto rounded-lg md:max-w-none"
                loading="lazy"
              />
            </div>
          </div>
          <div
            v-if="i < boostList.length - 1"
            class="boost-arrow mt-12 mb-2 flex justify-center"
          >
            <img
              src="/boost/mobArrow@2x.png"
              alt=""
              aria-hidden="true"
              :class="[
                'md:hidden object-contain',
                i === 0 || i === 1 ? 'w-[100px] h-[184px]' : 'w-[79px] h-[145px]',
              ]"
            />
            <img
              src="/boost/arrow@2x.png"
              alt=""
              aria-hidden="true"
              :class="[
                'hidden md:block object-contain',
                i === 0 || i === 1 ? 'w-[380px] h-[190px]' : 'w-[282px] h-[141px]',
                i % 2 === 1 ? 'scale-x-[-1]' : '',
              ]"
            />
          </div>
        </li>
      </ul>

      <div
        class="mt-10 prose prose-gray max-w-none prose-p:mb-3 prose-a:text-success prose-a:underline hover:prose-a:no-underline"
        v-html="introHtml"
        @click="onIntroClick"
      />

      <p class="text-center mt-10">
        <Button
          v-if="!isLogin"
          id="getStartedImproveScoreToday"
          as="a"
          :href="linkHref('/signup?type=buyer')"
          variant="landing1"
          size="xl"
        >
          <Translate translate-key="how_it_works_list.btn" />
        </Button>
        <Button
          v-else-if="user?.cardholder"
          id="getStartedImproveScoreToday"
          as="a"
          :href="linkHref('/cardholder/card')"
          variant="landing1"
          size="xl"
        >
          <Translate translate-key="how_it_works_list.btn" />
        </Button>
        <Button
          v-else
          id="getStartedImproveScoreToday"
          as="a"
          :href="linkHref('/tradelines-for-sale?limit=50&offset=0')"
          variant="landing1"
          size="xl"
        >
          <Translate translate-key="how_it_works_list.btn" />
        </Button>
      </p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { NuxtLink } from "#components";
import { Button } from "~/shared/components/ui/button";
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import { Dialogs, useDialogsStore } from "~/stores/dialogStore";
import { useCurrentUserStore } from "~/stores/userStore";

const { t } = useI18n();
const { linkHref } = useNativeRouteLink();

function withLocaleLinks(html: string): string {
  if (!html) return html;
  return html
    .replace(
      /href="\/tradelines-for-sale([^"]*)"/gi,
      () => `href="${linkHref("/tradelines-for-sale?limit=50&offset=0")}"`,
    )
    .replace(
      /href="\/signup([^"]*)"/gi,
      () => `href="${linkHref("/signup?type=buyer")}"`,
    )
    .replace(/href="\/faq"/gi, () => `href="${linkHref("/faq")}"`);
}

const boostList = computed(() => [
  {
    id: "bl-1",
    img: "/boost/choose-your-tradelines.png",
    title: t("how_it_works_list.boostList.1.title"),
    textContent: withLocaleLinks(t("how_it_works_list.boostList.1.text")),
    alt: t("how_it_works_list.boostList.1.alt"),
    width: 430,
    height: 445,
  },
  {
    id: "bl-2",
    img: "/boost/purchase-your-tradelines.png",
    title: t("how_it_works_list.boostList.2.title"),
    textContent: withLocaleLinks(t("how_it_works_list.boostList.2.text")),
    alt: t("how_it_works_list.boostList.2.alt"),
    width: 430,
    height: 274,
  },
  {
    id: "bl-3",
    img: "/boost/increase-your-credit-score.png",
    title: t("how_it_works_list.boostList.3.title"),
    textContent: withLocaleLinks(t("how_it_works_list.boostList.3.text")),
    alt: t("how_it_works_list.boostList.3.alt"),
    width: 430,
    height: 327,
  },
]);

const introHtml = computed(() =>
  withLocaleLinks(t("how_it_works_list.text")),
);

function onIntroClick(e: MouseEvent) {
  const target = (e.target as HTMLElement).closest("#playVideoHowItWorks");
  if (target) {
    e.preventDefault();
    useDialogsStore().openDialog(Dialogs.QuizModal);
  }
}

const userStore = useCurrentUserStore();
const isLogin = computed(() => userStore.isAuthenticated);
const user = computed(() => userStore.currentUser);
</script>
